import React, { useState, useRef, useEffect } from 'react';
import {
  Input,
  Button,
  message,
} from 'antd';
import {
  SpText,
} from '../../widgetDesign';
import styled from 'styled-components';
import {
  axiosLoginInstance,
  BASE_URLS,
} from '../../api';
import {
  AUTH_CHALLENGES,
} from '../../appconstants';

type Props = {
  isMobileView: Boolean,
  userEmail: String,
  firstHint: String,
  firstSession: String,
  setShowOTP: Function,
  authSuccessCallback: Function,
  // setNotification: Function,
};

// $FlowFixMe
const OtpWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  paddingTop: '24px',
  paddingBottom: '24px',
  alignItems: 'center',
  width: '300px',
  minHeight: '520px',
  borderRadius: '10px',
  backgroundColor: 'transparent',
});

const OTP = (props: Props) => {
  const {
    isMobileView,
    userEmail,
    firstHint,
    firstSession,
    setShowOTP,
    authSuccessCallback,
    // setNotification,
  } = props;

  const [otp, setOTP] = useState({
    0: '',
    1: '',
    2: '',
    3: '',
    4: '',
    5: '',
  });
  const [enableResendButton, setEnableResendButton] = useState(false);
  const [hint, setHint] = useState('Remaining attempts: 3');
  const [currentSession, setCurrentSession] = useState('');
  const [loading, setLoading] = useState('');
  // init = first otp yet to be entered
  // resent = otp has been resend
  // fail = otp entered is wrong
  const [otpMessageStatus, setOtpMessageStatus] = useState('init');

  useEffect(() => {
    const timer = setTimeout(() => {
      setEnableResendButton(true);
    }, 60000);
    // Cleanup
    return () => clearTimeout(timer);
  }, [enableResendButton]);

  // Refs for each input field
  const inputRefs = {
    0: useRef(null),
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
    4: useRef(null),
    5: useRef(null),
  };

  // useEffect to set the first hint and session
  useEffect(() => {
    setHint(firstHint);
    // setHint('Remaining attempts: 3');
    setCurrentSession(firstSession);
  }, [firstSession, firstHint]);

  const handleInput = (fieldPos, value) => {
    // get the lenght of the value
    // it should be 1 or 6 digits
    // then parse as int and make sure the
    // 1 or 6 digit value is an int
    // if 1 digit then set current field alone
    // if 6 digit then set the value for all fields
    if (typeof value !== 'string') {
      return;
    }
    const processedValue = value.replace(/\s/g, '');
    const currentFieldPos = parseInt(fieldPos, 10);
    if (processedValue === '') {
      setOTP(state => ({ ...state, [fieldPos]: '' }));
      const prevFiledPos = currentFieldPos > 0 ? currentFieldPos - 1 : 0;
      inputRefs[prevFiledPos].current.focus();
      return;
    }
    const inputLength = processedValue?.length;
    if (inputLength !== 1
      && inputLength !== 2
      && inputLength !== 6) {
      return;
    }
    const parsedValue = parseInt(processedValue, 10);
    if (Number.isNaN(parsedValue)) {
      return;
    }
    const nextFiledPos = currentFieldPos < 5 ? currentFieldPos + 1 : 5;
    switch (inputLength) {
      case 1:
        setOTP(state => ({ ...state, [fieldPos]: processedValue }));
        inputRefs[nextFiledPos].current.focus();
        break;
      case 2:
        setOTP(state => ({ ...state, [fieldPos]: processedValue[1] }));
        inputRefs[nextFiledPos].current.focus();
        break;
      case 6:
        setOTP(() => ({
          0: processedValue[0],
          1: processedValue[1],
          2: processedValue[2],
          3: processedValue[3],
          4: processedValue[4],
          5: processedValue[5],
        }));
        break;
      default:
        break;
    }
  };

  const handleInputKeyDown = (fieldPos, event) => {
    const { key } = event;
    const currentPos = parseInt(fieldPos, 10);
    // Defer the check to after onChange has run
    if (key === 'Backspace' && otp[fieldPos] === '') {
      const prevPos = currentPos - 1;
      if (prevPos >= 0 && inputRefs[prevPos]?.current) {
        // Slight delay to avoid clashing with onChange
        setTimeout(() => {
          inputRefs[prevPos].current.focus();
        }, 10);
      }
    }
  };

  const handleResendOTP = async () => {
    // call api to resend OTP, but only after
    // timeout
    try {
      const response = await axiosLoginInstance.post(BASE_URLS.RESEND_OTP, {
        email: userEmail,
      });
      const data = response?.data;
      const entity = data?.entity;
      if (entity?.challengeName === AUTH_CHALLENGES.CUSTOM_CHALLENGE
        && entity?.session
      ) {
        setCurrentSession(entity.session);
        setHint(entity?.challengeParameters?.hint);
        setOTP({
          0: '',
          1: '',
          2: '',
          3: '',
          4: '',
          5: '',
        });
        setEnableResendButton(false);
        setOtpMessageStatus('resent');
      }
    } catch (e) {
      message.error('Could not resend OTP.');
    }
  };

  const handleOTPSubmit = async () => {
    // call api to submit otp and handle it's response
    setLoading(true);
    try {
      const otpValue = Object.keys(otp).sort().reduce((total, key) => `${total}${otp[key]}`, '');
      if (otpValue.length < 6) {
        message.error('Enter all OTP digits.');
        setLoading(false);
        return;
      }
      const reponse = await axiosLoginInstance.post(BASE_URLS.RESPOND_CHALLENGE, {
        challengeName: AUTH_CHALLENGES.CUSTOM_CHALLENGE,
        session: currentSession,
        challengeResponses: {
          ANSWER: otpValue,
          USERNAME: userEmail,
        },
      });
      const data = reponse?.data;
      const entity = data?.entity;
      // check if this message is correct
      // this is the exception case
      setLoading(false);
      if (data?.message === 'Unauthorized user') {
        message.error('Unauthorized');
        setOtpMessageStatus('fail');
        setShowOTP(false);
      } else if (entity?.session) {
        setCurrentSession(entity.session);
        setHint(entity?.challengeParameters?.hint);
        setOTP({
          0: '',
          1: '',
          2: '',
          3: '',
          4: '',
          5: '',
        });
        setOtpMessageStatus('fail');
      } else if (entity) {
        await authSuccessCallback(entity);
        setOtpMessageStatus('init');
        setShowOTP(false);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      // if (e.response && e.response.status !== 401) {
      //   setNotification({
      //     type: NOTIFICATION.ERROR,
      //     payload: e.response ? e.response.data.message : MESSAGES.API_ERROR,
      //   });
      // }
      setShowOTP(false);
      message.error('OTP Login Error.');
    }
  };

  const otpInputStyle = {
    width: isMobileView ? '40px' : '40px',
    height: isMobileView ? '40px' : '40px',
    textAlign: 'center',
    // width: isMobileView ? '10%' : '',
    // height: isMobileView ? '5vh' : '8vh',
    fontSize: isMobileView ? '35px' : '',
    border: '1px solid #ccc',
    borderRadius: '6px',
    backgroundColor: '#f2f2f2',
  };

  return (
    <OtpWrapper
      style={{
        width: isMobileView ? '400px' : '',
        height: isMobileView ? '600px' : '',
        paddingTop: isMobileView ? '0px' : '',
        paddingBottom: isMobileView ? '0px' : '',
      }}
    >
      {/* <img
        src={window.location.host.includes('qpaymentz') ? qpayments : swireLogo}
        alt="Swirepay Logo"
        style={{ width: isMobileView ? '420px' : '270px' }}
      /> */}
      {/* <SpText fontSize={isMobileView ? '35px' : '15px'}>Provide One Time Password</SpText> */}
      <div
        className="mb-3 py-5 mt-5"
        style={{
          width: isMobileView ? '100%' : '100%',
          // paddingLeft: isMobileView ? '4vw' : '',
          // paddingRight: isMobileView ? '4vw' : '',
          // paddingTop: isMobileView ? '1vh' : '',
          // paddingBottom: isMobileView ? '1vh' : '',
          // marginTop: isMobileView ? '1vh' : '5vh',
        }}
      >
        <div className="d-flex justify-content-start mt-2 mb-5">
          <SpText fontSize={isMobileView ? '35px' : '20px'}>
            <label htmlFor="" className="font-weight-bold" style={{ fontSize: isMobileView ? '35px' : '15px' }}>One Time Password</label>
          </SpText>
        </div>
        <div
          style={{
            display: 'flex',
            gap: isMobileView ? '20px' : '10px',
            justifyContent: 'center',
            width: isMobileView ? '100%' : '100%',
          }}
          className="mt-6 mb-3 pl-5 pr-5"
        >
          <Input
            ref={inputRefs['0']}
            style={otpInputStyle}
            value={otp['0']}
            onKeyDown={(e) => handleInputKeyDown('0', e)}
            onChange={(e) => handleInput('0', e.target.value)}
          />
          <Input
            ref={inputRefs['1']}
            style={otpInputStyle}
            value={otp['1']}
            onKeyDown={(e) => handleInputKeyDown('1', e)}
            onChange={(e) => handleInput('1', e.target.value)}
          />
          <Input
            ref={inputRefs['2']}
            style={otpInputStyle}
            value={otp['2']}
            onKeyDown={(e) => handleInputKeyDown('2', e)}
            onChange={(e) => handleInput('2', e.target.value)}
          />
          <Input
            ref={inputRefs['3']}
            style={otpInputStyle}
            value={otp['3']}
            onKeyDown={(e) => handleInputKeyDown('3', e)}
            onChange={(e) => handleInput('3', e.target.value)}
          />
          <Input
            ref={inputRefs['4']}
            style={otpInputStyle}
            value={otp['4']}
            onKeyDown={(e) => handleInputKeyDown('4', e)}
            onChange={(e) => handleInput('4', e.target.value)}
          />
          <Input
            ref={inputRefs['5']}
            style={otpInputStyle}
            value={otp['5']}
            onKeyDown={(e) => handleInputKeyDown('5', e)}
            onChange={(e) => handleInput('5', e.target.value)}
          />
        </div>
        {
          otpMessageStatus === 'fail' && (
            <div className="d-flex justify-content-start mt-2">
              <SpText fontSize={isMobileView ? '35px' : '13px'} color="red">
                <strong>OTP entered is incorrect.</strong>
              </SpText>
            </div>
          )
        }
        {
          otpMessageStatus === 'fail' && hint && hint !== '' && (
            <div className="d-flex justify-content-start mt-2">
              <SpText fontSize={isMobileView ? '35px' : '13px'}>Please enter the correct <strong>OTP</strong>. {hint.toLowerCase()}</SpText>
            </div>
          )
        }
        {
          otpMessageStatus === 'init' && (
            <div className="d-flex justify-content-start mt-2">
              <SpText fontSize={isMobileView ? '25px' : '13px'}>Please provide OTP sent to <strong>{userEmail}</strong></SpText>
            </div>
          )
        }
        {
          otpMessageStatus === 'resent' && (
            <div className="d-flex justify-content-start mt-2">
              <SpText fontSize={isMobileView ? '35px' : '13px'}><strong>OTP</strong> resent. {hint.toLowerCase()}</SpText>
            </div>            
          )
        }
        <div className="d-flex justify-content-start align-items-center mt-1">
          <SpText fontSize={isMobileView ? '25px' : '13px'}><strong>Did not receive one?</strong></SpText>
          <Button
            type="link"
            onClick={handleResendOTP}
            disabled={!enableResendButton}
            style={{
              fontSize: isMobileView ? '25px' : '13px',
            }}
          >
            Resend
          </Button>
        </div>
        <div
          className="d-flex justify-content-center mt-5"
          style={{
            // marginTop: isMobileView ? '2vh' : '5vh',
          }}
        >
          <Button
            type="primary"
            shape="round"
            onClick={handleOTPSubmit}
            loading={loading}
            style={{
              width: isMobileView ? '70px' : '',
              height: isMobileView ? '50px' : '',
              fontSize: isMobileView ? '40px' : '15px',
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </OtpWrapper>
  );
};

export default OTP;
